import { ADDONS, type PlanName, type AddonKey } from '~/composables/usePlanConfig'

export type CurrencyCode = 'EUR' | 'USD' | 'GBP'

interface CurrencyConfig {
  symbol: string
}

const CURRENCY_SYMBOLS: Record<CurrencyCode, CurrencyConfig> = {
  EUR: { symbol: '€' },
  USD: { symbol: '$' },
  GBP: { symbol: '£' }
}

const FALLBACK_RATES: Record<string, number> = { USD: 1.08, GBP: 0.85 }

export const PRICES_EUR = {
  pro: { monthly: 4.99, yearly: 44.99, lifetime: 6.99 },
  elite: { monthly: 9.99, yearly: 89.99, lifetime: 12.99 }
} as const

export type BillingTier = 'monthly' | 'yearly' | 'lifetime'

export function useCurrency() {
  const currency = useState<CurrencyCode>('currency', () => {
    if (import.meta.server) return 'EUR'
    const saved = localStorage.getItem('preferred-currency') as CurrencyCode | null
    if (saved && saved in CURRENCY_SYMBOLS) return saved
    return 'EUR'
  })

  // Fetch live rates from our server endpoint (cached server-side for 6h)
  const { data: ratesData } = useAsyncData('currency-rates', () =>
    $fetch<{ base: string, rates: Record<string, number> }>('/api/currency/rates'),
  { server: false, lazy: true }
  )

  const rates = computed(() => ratesData.value?.rates ?? FALLBACK_RATES)

  function setCurrency(code: CurrencyCode) {
    currency.value = code
    if (import.meta.client) {
      localStorage.setItem('preferred-currency', code)
    }
  }

  function getRate(): number {
    if (currency.value === 'EUR') return 1
    return rates.value[currency.value] ?? FALLBACK_RATES[currency.value] ?? 1
  }

  function convertPrice(eurPrice: number): number {
    return Math.round(eurPrice * getRate() * 100) / 100
  }

  function formatPrice(plan: PlanName, billingCycle: BillingTier): string {
    const { symbol } = CURRENCY_SYMBOLS[currency.value]
    if (plan === 'free') return `${symbol}0`
    const eurPrice = PRICES_EUR[plan][billingCycle]
    const converted = convertPrice(eurPrice)
    const formatted = converted % 1 === 0 ? converted.toString() : converted.toFixed(2)
    return `${symbol}${formatted}`
  }

  function getYearlySavings(plan: PlanName): string | null {
    if (plan === 'free') return null
    const monthly = PRICES_EUR[plan].monthly * 12
    const yearly = PRICES_EUR[plan].yearly
    const pct = Math.ceil((1 - yearly / monthly) * 10) * 10
    return `Save ${pct}%`
  }

  function formatAddonPrice(addon: AddonKey): string {
    const { symbol } = CURRENCY_SYMBOLS[currency.value]
    const converted = convertPrice(ADDONS[addon].monthlyEur)
    const formatted = converted % 1 === 0 ? converted.toString() : converted.toFixed(2)
    return `${symbol}${formatted}`
  }

  function getUpgradePrice(fromPlan: Exclude<PlanName, 'free'>, toPlan: Exclude<PlanName, 'free'>): string {
    const { symbol } = CURRENCY_SYMBOLS[currency.value]
    const fromEur = PRICES_EUR[fromPlan].lifetime
    const toEur = PRICES_EUR[toPlan].lifetime
    const diffEur = Math.max(0, toEur - fromEur)
    const converted = convertPrice(diffEur)
    const formatted = converted % 1 === 0 ? converted.toString() : converted.toFixed(2)
    return `${symbol}${formatted}`
  }

  return {
    currency,
    currencies: Object.keys(CURRENCY_SYMBOLS) as CurrencyCode[],
    setCurrency,
    formatPrice,
    formatAddonPrice,
    getYearlySavings,
    getUpgradePrice
  }
}
